import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import RatingModal from './RatingModal';

const OrderHistoryCard = ({ order, onReorder, onViewDetails, onTrackOrder }) => {
  const [showRatingModal, setShowRatingModal] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered':
        return 'bg-success/10 text-success';
      case 'in-transit':
        return 'bg-warning/10 text-warning';
      case 'preparing':
        return 'bg-primary/10 text-primary';
      case 'cancelled':
        return 'bg-destructive/10 text-destructive';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return 'CheckCircle2';
      case 'in-transit':
        return 'Truck';
      case 'preparing':
        return 'ChefHat';
      case 'cancelled':
        return 'XCircle';
      default:
        return 'Clock';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div className="bg-card rounded-xl shadow-warm p-4 md:p-5 hover:shadow-warm-md transition-smooth">
      <div className="flex items-start gap-3 md:gap-4 mb-4">
        <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden flex-shrink-0">
          <Image
            src={order?.dishImage}
            alt={order?.dishImageAlt}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h4 className="font-heading font-semibold text-base md:text-lg line-clamp-1">
              {order?.dishName}
            </h4>
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1 flex-shrink-0 ${getStatusColor(order?.status)}`}>
              <Icon name={getStatusIcon(order?.status)} size={12} />
              {order?.status?.charAt(0)?.toUpperCase() + order?.status?.slice(1)?.replace('-', ' ')}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-2">
            {order?.supplierName}
          </p>
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Icon name="Calendar" size={12} />
              {formatDate(order?.orderDate)}
            </span>
            <span className="flex items-center gap-1">
              <Icon name="Package" size={12} />
              {order?.quantity} plate{order?.quantity > 1 ? 's' : ''}
            </span>
            {order?.pricePerPlate !== undefined && (
              <span className="text-xs text-muted-foreground">• ${Number(order?.pricePerPlate ?? 0).toFixed(2)} each</span>
            )}
            <span className="font-heading font-bold text-foreground data-text">
              ${Number(order?.totalAmount ?? 0).toFixed(2)}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 pt-3 border-t border-border">
        {order?.status === 'delivered' && (
          <>
            <Button
              variant="outline"
              size="sm"
              fullWidth
              iconName="RotateCcw"
              onClick={() => onReorder(order)}
            >
              Reorder
            </Button>
            <Button
              variant="ghost"
              size="sm"
              fullWidth
              iconName="Star"
              onClick={() => setShowRatingModal(true)}
            >
              Rate
            </Button>
          </>
        )}
        {(order?.status === 'in-transit' || order?.status === 'preparing') && (
          <>
            <Button
              variant="default"
              size="sm"
              fullWidth
              iconName="MapPin"
              onClick={() => onTrackOrder(order)}
            >
              Track Order
            </Button>
            <Button
              variant="outline"
              size="sm"
              fullWidth
              iconName="MessageSquare"
              onClick={() => onViewDetails(order)}
            >
              Chat
            </Button>
          </>
        )}
        {order?.status === 'cancelled' && (
          <Button
            variant="outline"
            size="sm"
            fullWidth
            iconName="Eye"
            onClick={() => onViewDetails(order)}
          >
            View Details
          </Button>
        )}
      </div>

      <RatingModal
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        order={order}
        onRatingSubmitted={() => {
          setShowRatingModal(false);
        }}
      />
    </div>
  );
};

export default OrderHistoryCard;